import React, { useEffect, useState } from 'react';

import { CalendarOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';

import clsx from 'clsx';
import addMonths from 'date-fns/addMonths';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import type { FormikProps } from 'formik';
import { Field, Form as FormikForm } from 'formik';
import { formatDate } from 'helpers/date';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import RadioSelection from 'v2/components/Shared/RadioSelection';
import { parseDdMmYyyy } from 'v2/formatters/date';
import { sendEventToGA } from 'v2/helpers/analytics';
import { useMutableOptions } from 'v2/hooks/useMutableOptions';
import type { EFinancialStatementTabs, FinancialStatementFilter } from 'v2/interfaces/financial';
import { MovementsType, SearchType, TransactionsType } from 'v2/interfaces/financial';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import {
  MovementTypesFilter,
  PresetRanges,
  SearchTypeFilter,
  TransactionTypesFilter,
  calendarPreset
} from './constants';

interface IFormProps extends FormikProps<FinancialStatementFilter> {
  currentTab: EFinancialStatementTabs;
}

const translateFieldName = (field: string) => {
  if (field === 'periodPreset') return 'Últimos Lançamentos';
  if (field === 'movement_type') return 'Movimentações';
  if (field === 'transaction_type') return 'Tipo da Transação';
  return 'Busque por';
};

function Form({ initialValues, values, currentTab, setFieldValue, errors }: IFormProps) {
  const [oldValues, setOldValues] = useState<FinancialStatementFilter>(initialValues);
  const [isSearchSent, setIsSearchSent] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleToggleCalendarOpen = () => {
    if (!isStatementTab) return;

    if (!isCalendarOpen) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Filtro',
        label: 'PesquisaPorPeriodo'
      });
    }

    setIsCalendarOpen(isOpen => !isOpen);
  };

  const isStatementTab = currentTab === 'statement';

  const transactionTypesFilter = useMutableOptions({
    options: TransactionTypesFilter,
    callback: option => values.movement_type === MovementsType.ALL || option.type.includes(values.movement_type)
  });

  const transactionEqualMovementType = transactionTypesFilter.filter(
    transaction => transaction.value === values.transaction_type
  );

  const handlePeriodPresetChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setFieldValue('periodPreset', value);
  };

  const handleSearch = () => {
    financialStatementService.setFilter(values);
    setIsSearchSent(true);
  };

  const PlaceholderForSearchType = (searchType: SearchType) => {
    const SearchTypeSelected = SearchTypeFilter.find(searchTypeValue => searchTypeValue.value === searchType);
    return SearchTypeSelected?.placeholder;
  };

  useEffect(() => {
    if (isStatementTab) {
      if (transactionEqualMovementType.length === 0) {
        setFieldValue('transaction_type', TransactionsType.ALL);
      }

      if (values.dates?.[0] && values.dates?.[1]) {
        const start = startOfDay(parseDdMmYyyy(values.dates[0]));
        const end = startOfDay(parseDdMmYyyy(values.dates[1]));
        const today = startOfDay(new Date());
        const isOrdered = !isBefore(end, start);
        const isNotFuture = !isAfter(start, today) && !isAfter(end, today);
        const isWithinMaxRange = !isAfter(end, startOfDay(addMonths(start, 6)));

        if (isOrdered && isNotFuture && isWithinMaxRange) {
          financialStatementService.setFilter({
            periodPreset: values.periodPreset,
            dates: values.dates,
            movement_type: values.movement_type,
            transaction_type: values.transaction_type
          });
        }
      }

      setOldValues(values);

      Object.keys(values).forEach((field, index) => {
        const oldValue = values[field as keyof FinancialStatementFilter];
        const newValue = Object.entries(oldValues)[index][1];

        if (oldValue !== newValue) {
          sendEventToGA({
            category: 'Novo Extrato',
            action: 'Filtro',
            label: translateFieldName(field[0])
          });
        }
      });
    }
  }, [values, isStatementTab, setFieldValue, oldValues, transactionEqualMovementType.length]);

  useEffect(() => {
    if (values.search_text === '' && isSearchSent) {
      financialStatementService.setFilter({
        ...values,
        search_type: SearchType.ALL
      });
      setIsSearchSent(false);
    }
  }, [isSearchSent, values, values.search_text]);

  useEffect(() => {
    if (values.search_type === SearchType.ALL && !errors.dates) {
      setFieldValue('search_text', '');
    }
  }, [setFieldValue, values.search_type, errors.dates]);

  useEffect(() => {
    setFieldValue('dates', [
      formatDate(subDays(new Date(), Number(values.periodPreset) - 1), 'dd/MM/yyyy'),
      formatDate(new Date(), 'dd/MM/yyyy')
    ]);
  }, [setFieldValue, values.periodPreset, isCalendarOpen]);

  useEffect(() => {
    if (isStatementTab) return;

    setIsCalendarOpen(false);

    const resetDates = [formatDate(subDays(new Date(), 6), 'dd/MM/yyyy'), formatDate(new Date(), 'dd/MM/yyyy')];

    setFieldValue('movement_type', MovementsType.ALL);
    setFieldValue('transaction_type', TransactionsType.ALL);
    setFieldValue('periodPreset', '7');
    setFieldValue('dates', resetDates);

    financialStatementService.setFilter({
      movement_type: MovementsType.ALL,
      transaction_type: TransactionsType.ALL,
      periodPreset: '7',
      dates: resetDates
    });
  }, [isStatementTab, setFieldValue]);

  return (
    <FormikForm>
      <div id='financial-filter'>
        <Row gutter={[16, 16]} align='top'>
          <Col xs={24} md={12}>
            <div className='w-full'>
              <div className='flex gap-4 [&>p]:m-0 [&>p]:leading-none'>
                <label className='h-[20px]! text-[15px]' id='periodPreset'>
                  Últimos lançamentos
                </label>
                {errors.dates && !isCalendarOpen && <span className='text-xs text-red-500'>{errors.dates}</span>}
              </div>
              <div className='mt-1 flex flex-row items-center justify-evenly'>
                {!isCalendarOpen && (
                  <div className='flex-1 [&_.ant-radio-group]:my-0 [&>div>div:first-child]:hidden'>
                    <RadioSelection
                      formLabel=''
                      options={PresetRanges}
                      selectedValue={values.periodPreset}
                      handleSelected={handlePeriodPresetChange}
                      isDisabled={!isStatementTab}
                    />
                  </div>
                )}

                {isCalendarOpen && (
                  <div className='w-full flex-1'>
                    <Field
                      id='calendar'
                      label=''
                      name='dates'
                      customPreset={calendarPreset}
                      component={DateRangePickerFormField}
                    />
                  </div>
                )}

                <div
                  className={clsx(
                    'ml-2 flex self-stretch rounded bg-[#b0bec5] px-4 text-white',
                    isStatementTab ? 'hover:bg-primary cursor-pointer' : 'cursor-not-allowed opacity-50',
                    isCalendarOpen && isStatementTab && 'hover:bg-[#f44336]'
                  )}
                  onClick={handleToggleCalendarOpen}
                  aria-disabled={!isStatementTab}
                >
                  {isCalendarOpen ? (
                    <CloseOutlined style={{ fontSize: 20 }} />
                  ) : (
                    <CalendarOutlined style={{ fontSize: 20 }} />
                  )}
                </div>
              </div>
            </div>
          </Col>

          <Col xs={24} md={6}>
            <Field
              id='movement_type'
              name='movement_type'
              label='Movimentações'
              options={MovementTypesFilter}
              component={SelectFormField}
              disabled={!isStatementTab}
              fullWidth
            />
          </Col>

          <Col xs={24} md={6}>
            <Field
              id='transaction_type'
              name='transaction_type'
              label='Tipo da transação'
              options={transactionTypesFilter}
              component={SelectFormField}
              disabled={!isStatementTab}
              fullWidth
            />
          </Col>
        </Row>

        <Row gutter={16} className='mt-2.5'>
          <Col xs={24} md={6}>
            <Field
              id='search_type'
              name='search_type'
              label='Busque por'
              options={SearchTypeFilter}
              component={SelectFormField}
              fullWidth
            />
          </Col>

          <Col xs={24} md={14}>
            <Field
              id='search_text'
              name='search_text'
              label='&nbsp;'
              placeholder={PlaceholderForSearchType(values.search_type) || 'Digite os dados da sua busca'}
              mask={values.search_type === SearchType.DOCUMENT ? 'document' : null}
              component={TextFormField}
              fullWidth
              disabled={values.search_type === SearchType.ALL}
            />
          </Col>

          <Col xs={24} md={4}>
            &nbsp;
            <Button
              icon={<SearchOutlined style={{ fontSize: 20 }} />}
              type='primary'
              block
              disabled={values.search_type === SearchType.ALL || !values.search_text || !!errors.search_text}
              onClick={handleSearch}
            >
              Buscar
            </Button>
          </Col>
        </Row>
      </div>
    </FormikForm>
  );
}

export default Form;
